import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { canAccessFatturazioneElettronica } from "../utils/fatturazioneAccess";
import { Navigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import AnteprimaFatturaLive from "../components/AnteprimaFatturaLive";
import { getLayout, saveLayout, uploadLogo, deleteLogo, anteprimaPdf } from "../utils/aspettoFatturaApi";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  Save, Loader2, Upload, Trash2, Monitor, Smartphone,
  Printer, FileSearch, Palette, Check, ChevronDown, ChevronUp,
  FileText, Receipt, Landmark, PenLine, QrCode, Pencil, CalendarClock,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── palette preset ───────────────────────────────────────────────
const COLOR_PRESETS = [
  { label: "Grigio",   value: "#6b7280" },
  { label: "Verde",    value: "#16a34a" },
  { label: "Blu",      value: "#1d4ed8" },
  { label: "Teal",     value: "#0d9488" },
  { label: "Viola",    value: "#7c3aed" },
  { label: "Rosso",    value: "#dc2626" },
];

// ── template mini-preview (SVG simbolico) ────────────────────────
const TemplateThumbs = {
  classico: ({ acc }) => (
    <svg viewBox="0 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="72" height="50" fill="#f8fafc" rx="3"/>
      {/* logo box */}
      <rect x="4" y="4" width="14" height="8" rx="1" fill="#e5e7eb"/>
      {/* company lines right */}
      <rect x="42" y="5" width="26" height="2" rx="1" fill={acc}/>
      <rect x="46" y="9" width="22" height="1.5" rx="1" fill="#d1d5db"/>
      <rect x="48" y="12" width="20" height="1.5" rx="1" fill="#d1d5db"/>
      {/* divider */}
      <rect x="4" y="16" width="64" height="1.5" rx="0.5" fill={acc}/>
      <rect x="4" y="18" width="64" height="1.5" rx="0.5" fill={acc} opacity="0.4"/>
      {/* title */}
      <rect x="16" y="21" width="40" height="2.5" rx="1" fill="#374151"/>
      {/* table header */}
      <rect x="4" y="26" width="64" height="5" rx="1" fill={acc}/>
      <rect x="6" y="27.5" width="20" height="2" rx="0.5" fill="white" opacity="0.8"/>
      {/* rows */}
      <rect x="4" y="32" width="64" height="4" rx="0" fill="#f9fafb"/>
      <rect x="4" y="36" width="64" height="4" rx="0" fill="white"/>
      <rect x="4" y="40" width="64" height="4" rx="0" fill="#f9fafb"/>
      {/* borders */}
      <rect x="4" y="32" width="64" height="0.5" fill="#e5e7eb"/>
      <rect x="4" y="36" width="64" height="0.5" fill="#e5e7eb"/>
      <rect x="4" y="40" width="64" height="0.5" fill="#e5e7eb"/>
      <rect x="4" y="44" width="64" height="0.5" fill="#e5e7eb"/>
      {/* totale */}
      <rect x="44" y="46" width="24" height="2.5" rx="1" fill={acc} opacity="0.7"/>
    </svg>
  ),
  moderno: ({ acc }) => (
    <svg viewBox="0 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="72" height="50" fill="#f8fafc" rx="3"/>
      {/* card header con barra sx */}
      <rect x="0" y="3" width="72" height="14" fill="#f1f5f9" rx="2"/>
      <rect x="0" y="3" width="4" height="14" rx="1" fill={acc}/>
      <rect x="7" y="5" width="14" height="7" rx="1" fill="#e5e7eb"/>
      <rect x="42" y="6" width="26" height="2.5" rx="1" fill={acc}/>
      <rect x="46" y="10" width="22" height="1.5" rx="1" fill="#94a3b8"/>
      {/* title pill */}
      <rect x="12" y="19" width="48" height="6" rx="3" fill={acc}/>
      <rect x="18" y="20.5" width="36" height="3" rx="1.5" fill="white" opacity="0.7"/>
      {/* dest card */}
      <rect x="4" y="27" width="64" height="9" rx="2" fill="white"/>
      <rect x="4" y="27" width="3" height="9" rx="1" fill={acc}/>
      <rect x="9" y="29" width="22" height="2" rx="1" fill="#374151"/>
      <rect x="9" y="33" width="16" height="1.5" rx="1" fill="#94a3b8"/>
      <rect x="38" y="29" width="20" height="2" rx="1" fill="#374151"/>
      <rect x="38" y="33" width="14" height="1.5" rx="1" fill="#94a3b8"/>
      {/* tabella no-border */}
      <rect x="4" y="37" width="64" height="2.5" rx="0" fill="none" stroke={acc} strokeWidth="1.5"/>
      <rect x="4" y="40.5" width="64" height="3" rx="0" fill="white"/>
      <rect x="4" y="43.5" width="64" height="0.5" fill="#f1f5f9"/>
      {/* totale pill */}
      <rect x="44" y="46" width="24" height="4" rx="2" fill={acc}/>
      <rect x="47" y="47.5" width="18" height="1.5" rx="0.75" fill="white" opacity="0.8"/>
    </svg>
  ),
  sanitario: ({ acc }) => (
    <svg viewBox="0 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="72" height="50" fill="#f8fafc" rx="3"/>
      {/* banner colorato top */}
      <rect x="0" y="0" width="72" height="14" rx="3" fill={acc}/>
      <rect x="4" y="3" width="14" height="7" rx="1" fill="white" opacity="0.25"/>
      <rect x="22" y="4" width="28" height="2.5" rx="1" fill="white" opacity="0.9"/>
      <rect x="22" y="8" width="20" height="1.5" rx="1" fill="white" opacity="0.6"/>
      <rect x="22" y="11" width="16" height="1.5" rx="1" fill="white" opacity="0.5"/>
      {/* titolo con underline */}
      <rect x="4" y="17" width="30" height="2.5" rx="1" fill="#374151"/>
      <rect x="4" y="21" width="64" height="1" rx="0.5" fill={acc}/>
      {/* dest box verde */}
      <rect x="4" y="23" width="64" height="9" rx="2" fill="#f0fdf4"/>
      <rect x="4" y="23" width="64" height="9" rx="2" stroke="#bbf7d0" strokeWidth="0.75"/>
      <rect x="7" y="25" width="20" height="2" rx="1" fill="#374151"/>
      <rect x="7" y="29" width="14" height="1.5" rx="1" fill="#6b7280"/>
      <rect x="42" y="25" width="20" height="2" rx="1" fill="#374151"/>
      <rect x="42" y="29" width="16" height="1.5" rx="1" fill="#6b7280"/>
      {/* tabella con bordi */}
      <rect x="4" y="33" width="64" height="4.5" rx="1" fill={acc}/>
      <rect x="6" y="34.5" width="20" height="1.5" rx="0.5" fill="white" opacity="0.8"/>
      <rect x="4" y="38" width="64" height="3.5" rx="0" fill="#f9fafb"/>
      <rect x="4" y="41.5" width="64" height="3.5" rx="0" fill="white"/>
      {/* line separators vert */}
      <rect x="40" y="33" width="0.5" height="12" fill="white" opacity="0.3"/>
      <rect x="55" y="33" width="0.5" height="12" fill="white" opacity="0.3"/>
      {/* totale */}
      <rect x="44" y="46" width="24" height="2.5" rx="1" fill={acc} opacity="0.7"/>
    </svg>
  ),
  minimal: ({ acc }) => (
    <svg viewBox="0 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="72" height="50" fill="white" rx="3"/>
      {/* logo centrato */}
      <rect x="26" y="3" width="20" height="7" rx="1" fill="#f3f4f6"/>
      {/* nome centrato letterspacing */}
      <rect x="14" y="12" width="44" height="2" rx="1" fill="#374151" opacity="0.7"/>
      <rect x="20" y="15" width="32" height="1.5" rx="1" fill="#9ca3af"/>
      {/* divider sottile */}
      <rect x="4" y="19" width="64" height="0.5" fill="#e5e7eb"/>
      {/* titolo sx */}
      <rect x="4" y="22" width="10" height="1.5" rx="1" fill="#9ca3af"/>
      <rect x="4" y="25" width="36" height="3" rx="1" fill="#374151"/>
      {/* tabella no-border */}
      <rect x="4" y="31" width="64" height="1.5" rx="0.5" fill={acc} opacity="0.8"/>
      <rect x="4" y="34" width="64" height="3" rx="0" fill="white"/>
      <rect x="4" y="37" width="64" height="0.5" fill="#f3f4f6"/>
      <rect x="4" y="38" width="64" height="3" rx="0" fill="white"/>
      <rect x="4" y="41" width="64" height="0.5" fill="#f3f4f6"/>
      {/* totale grande sx */}
      <rect x="44" y="43" width="24" height="5" rx="1" fill="none"/>
      <rect x="50" y="44" width="18" height="4" rx="1" fill={acc} opacity="0.15"/>
      <rect x="52" y="45" width="14" height="2" rx="1" fill={acc} opacity="0.9"/>
    </svg>
  ),
  premium: ({ acc }) => (
    <svg viewBox="0 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="72" height="50" fill="#f8fafc" rx="3"/>
      {/* header bipartito */}
      <rect x="0" y="0" width="30" height="20" rx="2" fill={acc}/>
      <rect x="30" y="0" width="42" height="20" rx="2" fill="white"/>
      <rect x="30" y="0" width="42" height="20" fill="none" stroke="#e5e7eb" strokeWidth="0.75"/>
      {/* logo nel pannello sx */}
      <rect x="4" y="3" width="14" height="7" rx="1" fill="white" opacity="0.25"/>
      {/* testo bianco sx */}
      <rect x="4" y="12" width="20" height="2" rx="1" fill="white" opacity="0.9"/>
      <rect x="4" y="15" width="16" height="1.5" rx="1" fill="white" opacity="0.5"/>
      {/* dati dx */}
      <rect x="34" y="4" width="22" height="2.5" rx="1" fill="#374151"/>
      <rect x="34" y="8" width="18" height="1.5" rx="1" fill="#94a3b8"/>
      <rect x="34" y="11" width="14" height="1.5" rx="1" fill="#94a3b8"/>
      <rect x="34" y="15" width="20" height="2" rx="1" fill="#374151"/>
      <rect x="34" y="18" width="14" height="1.5" rx="1" fill="#94a3b8"/>
      {/* tabella con bordi arrotondati */}
      <rect x="4" y="22" width="64" height="5" rx="1.5" fill={acc}/>
      <rect x="6" y="23.5" width="20" height="2" rx="0.5" fill="white" opacity="0.8"/>
      <rect x="4" y="27" width="64" height="4" rx="0" fill="white"/>
      <rect x="4" y="31" width="64" height="4" rx="0" fill={acc} opacity="0.08"/>
      <rect x="4" y="35" width="64" height="4" rx="0" fill="white"/>
      {/* totale card */}
      <rect x="44" y="41" width="24" height="7" rx="2" fill={acc}/>
      <rect x="47" y="43" width="8" height="1.5" rx="0.75" fill="white" opacity="0.6"/>
      <rect x="47" y="46" width="18" height="2" rx="1" fill="white" opacity="0.9"/>
    </svg>
  ),
  commerciale: ({ acc }) => (
    <svg viewBox="0 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="72" height="50" fill="#fff" rx="3"/>
      {/* logo box sx */}
      <rect x="4" y="4" width="16" height="10" rx="1" fill="none" stroke="#d1d5db" strokeWidth="0.75"/>
      <rect x="6" y="6" width="12" height="6" rx="0.5" fill="#e5e7eb"/>
      {/* cedente dx */}
      <rect x="38" y="5" width="30" height="2" rx="1" fill="#374151"/>
      <rect x="42" y="9" width="26" height="1.5" rx="1" fill="#94a3b8"/>
      <rect x="44" y="12" width="24" height="1.5" rx="1" fill="#94a3b8"/>
      {/* titolo dx */}
      <rect x="30" y="16" width="38" height="2.5" rx="1" fill="#374151"/>
      {/* destinatario */}
      <rect x="4" y="21" width="14" height="1.5" rx="1" fill="#94a3b8"/>
      <rect x="38" y="21" width="20" height="2" rx="1" fill="#374151"/>
      {/* tabella 3 col grigia */}
      <rect x="4" y="25" width="64" height="4.5" rx="0" fill="#e5e7eb"/>
      <rect x="6" y="26.5" width="24" height="1.5" rx="0.5" fill="#374151" opacity="0.6"/>
      <rect x="4" y="29.5" width="64" height="3" rx="0" fill="#f9fafb"/>
      <rect x="4" y="32.5" width="64" height="3" rx="0" fill="white"/>
      <rect x="52" y="25" width="0.5" height="10.5" fill="#d1d5db"/>
      <rect x="60" y="25" width="0.5" height="10.5" fill="#d1d5db"/>
      {/* pagamento + scadenze */}
      <rect x="4" y="36" width="28" height="1.5" rx="1" fill="#9ca3af"/>
      <rect x="4" y="39" width="22" height="1.5" rx="1" fill="#cbd5e1"/>
      <rect x="40" y="36" width="20" height="1.5" rx="1" fill="#9ca3af"/>
      <rect x="44" y="39" width="16" height="2" rx="1" fill={acc} opacity="0.7"/>
      {/* totale grande */}
      <rect x="50" y="43" width="18" height="3" rx="1" fill="#374151"/>
    </svg>
  ),
  fic: () => (
    <svg viewBox="0 0 72 50" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
      <rect width="72" height="50" fill="#fff" rx="3"/>
      {/* logo intestazione sx */}
      <rect x="4" y="3" width="13" height="8" rx="0.5" fill="#e5e7eb"/>
      {/* cedente + titolo dx */}
      <rect x="48" y="4" width="20" height="1.5" rx="0.75" fill="#828282"/>
      <rect x="44" y="6.5" width="24" height="1.2" rx="0.6" fill="#b5b5b5"/>
      <rect x="44" y="8.5" width="24" height="1.2" rx="0.6" fill="#b5b5b5"/>
      <rect x="38" y="11" width="30" height="1.8" rx="0.9" fill="#828282"/>
      <rect x="4" y="14.5" width="64" height="0.4" fill="#cecece"/>
      {/* CF / destinatario */}
      <rect x="4" y="16.5" width="16" height="1.2" rx="0.6" fill="#6a6a6a"/>
      <rect x="38" y="16.5" width="14" height="1.2" rx="0.6" fill="#6a6a6a"/>
      <rect x="38" y="18.5" width="18" height="1.2" rx="0.6" fill="#b5b5b5"/>
      {/* tabella descrizione / importo */}
      <rect x="4" y="22" width="64" height="0.4" fill="#cecece"/>
      <rect x="4" y="24.5" width="64" height="3.5" fill="#eeeeee"/>
      <rect x="4" y="24.5" width="64" height="0.4" fill="#cecece"/>
      <rect x="4" y="28" width="64" height="0.4" fill="#cecece"/>
      <rect x="61" y="22" width="0.4" height="6" fill="#cecece"/>
      <rect x="6" y="25.6" width="22" height="1.3" rx="0.6" fill="#6a6a6a"/>
      {/* note */}
      <rect x="4" y="30.5" width="26" height="1.2" rx="0.6" fill="#6a6a6a"/>
      <rect x="4" y="32.5" width="18" height="1.2" rx="0.6" fill="#b5b5b5"/>
      {/* riepilogo + totale in fondo */}
      <rect x="4" y="37" width="64" height="0.4" fill="#cecece"/>
      <rect x="4" y="44" width="64" height="0.4" fill="#cecece"/>
      <rect x="44" y="37" width="0.4" height="7" fill="#cecece"/>
      <rect x="54" y="40.5" width="14" height="2.5" rx="1" fill="#828282"/>
      {/* logo piè di pagina dx */}
      <rect x="44" y="46" width="16" height="1.2" rx="0.6" fill="#b5b5b5"/>
      <rect x="62" y="45.3" width="6" height="3.5" rx="0.5" fill="#e5e7eb"/>
    </svg>
  ),
};

const TEMPLATE_IDS = ["classico", "moderno", "sanitario", "minimal", "premium", "commerciale", "fic"];
const TEMPLATE_LABELS = {
  classico: "Classico",
  moderno: "Moderno",
  sanitario: "Sanitario",
  minimal: "Minimal",
  premium: "Premium",
  commerciale: "Commerciale",
  fic: "Fatture in Cloud",
};

const DEFAULT_LAYOUT = {
  template: "classico",
  colore_principale: "#6b7280",
  intestazione: "compatta",
  mostra_nome_struttura: true,
  mostra_indirizzo: true,
  mostra_piva: true,
  mostra_email: true,
  mostra_telefono: false,
  mostra_sito_web: false,
  stile_tabella: "leggibile",
  blocco_logo: true,
  blocco_coordinate_bancarie: false,
  blocco_firma: false,
  blocco_qr_code: false,
  blocco_note_automatiche: true,
  blocco_scadenze: true,
  blocco_riepilogo_iva_dettagliato: true,
  blocco_timbro: false,
  footer_text: "",
  note_automatiche_text: "",
  logo_base64: null,
  logo_content_type: null,
  logo_larghezza_cm: 4.5,
  logo_footer_base64: null,
  logo_footer_content_type: null,
  contatti_sito_web: "",
  contatti_email: "",
  contatti_telefono: "",
  contatti_cellulare: "",
  bic: "",
};

// ── sub-componenti pannello ──────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 px-4 pt-5 pb-1.5 select-none">
      {children}
    </p>
  );
}

function Divider() {
  return <div className="h-px bg-slate-100 mx-0" />;
}

function ToggleRow({ label, desc, checked, onChange }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors">
      <div>
        <p className="text-sm text-slate-700 leading-tight">{label}</p>
        {desc && <p className="text-xs text-slate-400 mt-0.5">{desc}</p>}
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange} />
    </div>
  );
}

function LogoSlot({ base64, contentType, uploading, onPick, onDelete, emptyLabel }) {
  if (base64) {
    return (
      <div className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg bg-slate-50">
        <img
          src={`data:${contentType || "image/png"};base64,${base64}`}
          alt="logo"
          className="h-10 max-w-[100px] object-contain"
        />
        <div className="flex flex-col gap-1 ml-auto">
          <button
            onClick={onPick}
            disabled={uploading}
            className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 px-2 py-1 rounded border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
          >
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            Cambia
          </button>
          <button
            onClick={onDelete}
            className="flex items-center gap-1 text-xs text-red-500 hover:text-red-600 px-2 py-1 rounded border border-slate-200 bg-white hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-3 h-3" />
            Rimuovi
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      onClick={onPick}
      disabled={uploading}
      className="w-full flex flex-col items-center justify-center gap-1.5 py-5 border-2 border-dashed border-slate-200 rounded-lg text-slate-400 hover:text-slate-500 hover:border-slate-300 hover:bg-slate-50 transition-all"
    >
      {uploading
        ? <Loader2 className="w-5 h-5 animate-spin" />
        : <Upload className="w-5 h-5" />}
      <span className="text-xs font-medium">{emptyLabel}</span>
      <span className="text-[10px]">PNG · JPG · WEBP &nbsp;max 2 MB</span>
    </button>
  );
}

function ModeButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all
        ${active ? "bg-white shadow-sm text-slate-800 border border-slate-200" : "text-slate-400 hover:text-slate-600"}`}
    >
      {children}
    </button>
  );
}

// ────────────────────────────────────────────────────────────────
export default function AspettoFattura({ embedded = false }) {
  const { user, token, activeStruttura } = useAuth();
  const canAccess = canAccessFatturazioneElettronica(user);
  const strutturaIdForLayout = useMemo(
    () => activeStruttura?.zohoRecordId || user?.struttura_id || user?.zohoRecordId || null,
    [activeStruttura?.zohoRecordId, user?.struttura_id, user?.zohoRecordId],
  );

  const [layout, setLayout]           = useState(DEFAULT_LAYOUT);
  const [config, setConfig]           = useState({});
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(null);
  const [customColor, setCustomColor] = useState("#6b7280");
  // Stato locale per i campi testo liberi (non propagano al layout ad ogni keystroke)
  const [noteText, setNoteText]       = useState(DEFAULT_LAYOUT.note_automatiche_text);
  const [footerText, setFooterText]   = useState(DEFAULT_LAYOUT.footer_text);
  const [textEditorOpen, setTextEditorOpen] = useState(false);
  const logoInputRef = useRef();
  const footerLogoInputRef = useRef();

  const set = useCallback((key, value) => {
    setLayout(prev => ({ ...prev, [key]: value }));
  }, []);

  // Debounce 400ms: aggiorna il layout (e quindi la preview) solo dopo pausa nella digitazione
  useEffect(() => {
    const t = setTimeout(() => set("note_automatiche_text", noteText), 400);
    return () => clearTimeout(t);
  }, [noteText, set]);

  useEffect(() => {
    const t = setTimeout(() => set("footer_text", footerText), 400);
    return () => clearTimeout(t);
  }, [footerText, set]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const configParams = strutturaIdForLayout ? { strutturaId: strutturaIdForLayout } : {};
    Promise.all([
      getLayout(token, strutturaIdForLayout),
      axios.get(`${API_URL}/api/fatturazione/config`, {
        headers: { Authorization: `Bearer ${token}` },
        params: configParams,
      }),
    ])
      .then(([lRes, cRes]) => {
        const l = lRes.layout || {};
        const sid = lRes.strutturaId || strutturaIdForLayout;
        setLayout({ ...DEFAULT_LAYOUT, ...l, strutturaId: sid });
        setNoteText(l.note_automatiche_text || "");
        setFooterText(l.footer_text || "");
        if (l.colore_principale && !COLOR_PRESETS.some(p => p.value === l.colore_principale)) {
          setCustomColor(l.colore_principale);
        }
        setConfig(cRes.data?.config || {});
      })
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  }, [token, strutturaIdForLayout]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveLayout(token, layout, strutturaIdForLayout);
      toast.success("Aspetto fattura salvato");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const logoFields = (tipo) => (tipo === "footer"
    ? ["logo_footer_base64", "logo_footer_content_type"]
    : ["logo_base64", "logo_content_type"]);

  const handleLogoUpload = (tipo) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(tipo);
    try {
      const res = await uploadLogo(token, file, strutturaIdForLayout, tipo);
      const [fieldB64, fieldCt] = logoFields(tipo);
      set(fieldB64, res.logo_base64);
      set(fieldCt, res.logo_content_type);
      if (tipo !== "footer") set("blocco_logo", true);
      toast.success("Logo caricato");
    } catch {
      toast.error("Errore nel caricamento logo (max 2 MB, PNG/JPG/WEBP)");
    } finally {
      setUploadingLogo(null);
      e.target.value = "";
    }
  };

  const handleDeleteLogo = (tipo) => async () => {
    try {
      await deleteLogo(token, strutturaIdForLayout, tipo);
      const [fieldB64, fieldCt] = logoFields(tipo);
      set(fieldB64, null);
      set(fieldCt, null);
      toast.success("Logo rimosso");
    } catch {
      toast.error("Errore nella rimozione del logo");
    }
  };

  const handlePdfPreview = async () => {
    setPdfLoading(true);
    try {
      const blob = await anteprimaPdf(token, layout, null, null, strutturaIdForLayout);
      window.open(URL.createObjectURL(blob), "_blank");
    } catch {
      toast.error("Errore nella generazione del PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const isCustomColor = !COLOR_PRESETS.some(p => p.value === layout.colore_principale);
  const isFic = layout.template === "fic";

  if (!canAccess) return <Navigate to="/fatturazione" replace />;

  if (loading) {
    return (
      <div className={embedded ? "py-12 flex justify-center" : "min-h-screen flex items-center justify-center bg-slate-50"}>
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className={embedded ? "flex flex-col flex-1 min-h-0 w-full" : "min-h-screen bg-slate-50"}>
      {embedded ? (
        <div className="flex flex-wrap items-center justify-end gap-2 mb-1">
          <Button variant="outline" size="sm" className="text-xs h-8" onClick={handlePdfPreview} disabled={pdfLoading}>
            {pdfLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileSearch className="w-3.5 h-3.5 mr-1" />}
            Anteprima PDF
          </Button>
          <Button size="sm" className="text-xs h-8 bg-[#4CAF50] hover:bg-[#388E3C]" onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
            Salva
          </Button>
        </div>
      ) : (
        <PageHeader
          title="Aspetto fattura"
          subtitle="Configura il layout visivo applicato a tutte le fatture della struttura"
          actions={
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handlePdfPreview} disabled={pdfLoading}>
                {pdfLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileSearch className="w-4 h-4 mr-1.5" />}
                Anteprima PDF
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Salva
              </Button>
            </div>
          }
        />
      )}

      <div
        className={
          embedded
            ? "flex flex-col lg:flex-row gap-5 items-stretch flex-1 min-h-0 w-full"
            : "w-full px-4 sm:px-6 py-6 flex gap-5 items-start"
        }
      >

        {/* ════════════════════════════════════════════
            PANNELLO CONFIGURAZIONE — colonna sinistra
        ════════════════════════════════════════════ */}
        <div
          className={
            embedded
              ? "w-full lg:w-[min(420px,38%)] lg:max-w-[480px] lg:flex-none flex flex-col flex-1 min-h-0 bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
              : "w-[420px] flex-none flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
          }
        >

          {/* Intestazione pannello */}
          <div className="px-4 py-3.5 border-b border-slate-100 flex items-center justify-between shrink-0">
            <span className="text-sm font-semibold text-slate-800">Impostazioni layout</span>
            <span className="text-xs text-slate-400">applicato a tutte le fatture</span>
          </div>

          {/* Scrollable body — occupa tutto lo spazio verticale tra header e Salva */}
          <div
            className="overflow-y-auto flex-1 min-h-0"
            style={embedded ? undefined : { maxHeight: "calc(100vh - 180px)" }}
          >

            {/* ── Layout documento ── */}
            <SectionLabel>Layout documento</SectionLabel>
            <div className="grid grid-cols-3 gap-2 px-4 pb-4">
              {TEMPLATE_IDS.map(tid => {
                const Thumb = TemplateThumbs[tid];
                const active = layout.template === tid;
                return (
                  <button
                    key={tid}
                    onClick={() => set("template", tid)}
                    className={`relative rounded-lg border-2 overflow-hidden transition-all text-left
                      ${active ? "border-blue-500 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    {/* thumbnail */}
                    <div className="aspect-[72/50] w-full p-1 bg-white">
                      <Thumb acc={layout.colore_principale} />
                    </div>
                    {/* label */}
                    <div className={`px-2 py-1 flex items-center justify-between text-[11px] font-medium border-t
                      ${active ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-slate-50 border-slate-100 text-slate-600"}`}>
                      {TEMPLATE_LABELS[tid]}
                      {active && <Check className="w-3 h-3 text-blue-500" />}
                    </div>
                  </button>
                );
              })}
            </div>

            <Divider />

            {/* ── Logo ── */}
            <SectionLabel>{isFic ? "Logo intestazione (in alto a sinistra)" : "Logo"}</SectionLabel>
            <div className="px-4 pb-4">
              <LogoSlot
                base64={layout.logo_base64}
                contentType={layout.logo_content_type}
                uploading={uploadingLogo === "principale"}
                onPick={() => logoInputRef.current?.click()}
                onDelete={handleDeleteLogo("principale")}
                emptyLabel="Clicca per caricare il logo"
              />
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleLogoUpload("principale")}
              />
              {layout.logo_base64 && (
                <div className="mt-3 space-y-2">
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span>Ampiezza logo in fattura</span>
                    <span className="font-medium tabular-nums text-slate-800">
                      {(layout.logo_larghezza_cm ?? 4.5).toFixed(1)} cm
                    </span>
                  </div>
                  <input
                    type="range"
                    min={2}
                    max={8}
                    step={0.5}
                    value={layout.logo_larghezza_cm ?? 4.5}
                    onChange={(e) => set("logo_larghezza_cm", parseFloat(e.target.value))}
                    className="w-full h-1.5 accent-blue-600 cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400">
                    <span>Piccolo</span>
                    <span>Grande</span>
                  </div>
                </div>
              )}
            </div>

            {isFic && (
              <>
                <Divider />

                {/* ── Piè di pagina (solo Fatture in Cloud) ── */}
                <SectionLabel>Piè di pagina (in basso a destra)</SectionLabel>
                <p className="text-[11px] text-slate-400 px-4 -mt-1 mb-2">
                  Logo più piccolo e contatti accanto al numero di pagina
                </p>
                <div className="px-4 pb-4 space-y-3">
                  <LogoSlot
                    base64={layout.logo_footer_base64}
                    contentType={layout.logo_footer_content_type}
                    uploading={uploadingLogo === "footer"}
                    onPick={() => footerLogoInputRef.current?.click()}
                    onDelete={handleDeleteLogo("footer")}
                    emptyLabel="Clicca per caricare il logo piè di pagina"
                  />
                  <input
                    ref={footerLogoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={handleLogoUpload("footer")}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "contatti_sito_web",  placeholder: "Sito web (es. www.struttura.it)" },
                      { key: "contatti_email",     placeholder: "Email (es. info@struttura.it)" },
                      { key: "contatti_telefono",  placeholder: "Telefono" },
                      { key: "contatti_cellulare", placeholder: "Cellulare" },
                    ].map(({ key, placeholder }) => (
                      <Input
                        key={key}
                        placeholder={placeholder}
                        value={layout[key] || ""}
                        onChange={e => set(key, e.target.value)}
                        className="text-xs bg-white h-8"
                        maxLength={80}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            <Divider />

            {/* ── Colore principale ── */}
            <SectionLabel>Colore principale</SectionLabel>
            {isFic && (
              <p className="text-[11px] text-slate-400 px-4 -mt-1 mb-2">
                Il modello Fatture in Cloud usa sempre la sua palette grigia
              </p>
            )}
            <div className="px-4 pb-4">
              <div className="grid grid-cols-6 gap-3 mb-3">
                {COLOR_PRESETS.map(p => (
                  <button
                    key={p.value}
                    title={p.label}
                    onClick={() => set("colore_principale", p.value)}
                    className="flex flex-col items-center gap-1 group"
                  >
                    <div
                      className={`w-10 h-10 rounded-xl transition-all shadow-sm
                        ${layout.colore_principale === p.value ? "ring-2 ring-offset-1 ring-blue-400 scale-110" : "hover:scale-105"}`}
                      style={{ background: p.value }}
                    />
                    <span className={`text-[9px] leading-tight text-center
                      ${layout.colore_principale === p.value ? "text-blue-600 font-semibold" : "text-slate-400"}`}>
                      {p.label}
                    </span>
                  </button>
                ))}
              </div>
              {/* Custom color picker */}
              <label className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all
                ${isCustomColor ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}>
                <div className="w-5 h-5 rounded border border-slate-300 overflow-hidden flex-shrink-0">
                  <input
                    type="color"
                    value={isCustomColor ? layout.colore_principale : customColor}
                    onChange={e => { setCustomColor(e.target.value); set("colore_principale", e.target.value); }}
                    className="w-8 h-8 -m-1.5 cursor-pointer border-none appearance-none"
                  />
                </div>
                <span className="text-xs text-slate-600 flex-1">
                  Colore personalizzato
                  {isCustomColor && <span className="ml-1 font-mono text-blue-600">{layout.colore_principale}</span>}
                </span>
                <Palette className="w-3.5 h-3.5 text-slate-400" />
              </label>
            </div>

            <Divider />

            {/* ── Intestazione ── */}
            <SectionLabel>Intestazione struttura</SectionLabel>
            <div className="px-4 pb-1">
              <div className="flex gap-1.5 p-1 bg-slate-100 rounded-lg mb-1">
                <ModeButton active={layout.intestazione === "compatta"} onClick={() => set("intestazione", "compatta")}>Compatta</ModeButton>
                <ModeButton active={layout.intestazione === "estesa"}   onClick={() => set("intestazione", "estesa")}>Estesa</ModeButton>
              </div>
            </div>
            <ToggleRow label="Nome struttura"  checked={layout.mostra_nome_struttura} onChange={v => set("mostra_nome_struttura", v)} />
            <ToggleRow label="Indirizzo"        checked={layout.mostra_indirizzo}      onChange={v => set("mostra_indirizzo", v)} />
            <ToggleRow label="P.IVA / C.F."     checked={layout.mostra_piva}           onChange={v => set("mostra_piva", v)} />
            {layout.intestazione === "estesa" && (
              <>
                <ToggleRow label="Email / PEC"  checked={layout.mostra_email}    onChange={v => set("mostra_email", v)} />
                <ToggleRow label="Telefono"     checked={layout.mostra_telefono} onChange={v => set("mostra_telefono", v)} />
                <ToggleRow label="Sito web"     checked={layout.mostra_sito_web} onChange={v => set("mostra_sito_web", v)} />
              </>
            )}
            <div className="pb-1" />

            <Divider />

            {/* ── Stile tabella righe ── */}
            <SectionLabel>Stile tabella righe</SectionLabel>
            <p className="text-[11px] text-slate-400 px-4 -mt-1 mb-2">Scegli la densità e lo stile della tabella prodotti/servizi</p>
            <div className="grid grid-cols-2 gap-3 px-4 pb-4">
              {[
                {
                  id: "compatta",
                  label: "Compatta",
                  desc: "Più righe per pagina",
                  icon: (
                    <svg viewBox="0 0 36 28" fill="none" className="w-8 h-6">
                      <rect width="36" height="28" fill="#f1f5f9" rx="2"/>
                      <rect x="2" y="2" width="32" height="3.5" rx="1" fill="#94a3b8"/>
                      {[7,11,15,19,23].map(y => (
                        <rect key={y} x="2" y={y} width="32" height="3" rx="0.5" fill={y%4===3?"#e2e8f0":"#f8fafc"}/>
                      ))}
                    </svg>
                  ),
                },
                {
                  id: "leggibile",
                  label: "Leggibile",
                  desc: "Maggiore spaziatura",
                  icon: (
                    <svg viewBox="0 0 36 28" fill="none" className="w-8 h-6">
                      <rect width="36" height="28" fill="#f1f5f9" rx="2"/>
                      <rect x="2" y="2" width="32" height="4" rx="1" fill="#94a3b8"/>
                      {[8,15,22].map(y => (
                        <rect key={y} x="2" y={y} width="32" height="5" rx="0.5" fill={y===15?"#e2e8f0":"#f8fafc"}/>
                      ))}
                    </svg>
                  ),
                },
              ].map(opt => {
                const active = layout.stile_tabella === opt.id;
                return (
                  <button
                    key={opt.id}
                    onClick={() => set("stile_tabella", opt.id)}
                    className={`relative flex flex-col items-start gap-2 rounded-xl border-2 p-3 text-left transition-all
                      ${active
                        ? "border-green-400 bg-green-50"
                        : "border-slate-200 bg-white hover:border-slate-300"}`}
                  >
                    {active && (
                      <span className="absolute top-2 right-2 flex items-center justify-center w-5 h-5 rounded-full bg-green-500">
                        <Check className="w-3 h-3 text-white" strokeWidth={3} />
                      </span>
                    )}
                    <div className="text-slate-500">{opt.icon}</div>
                    <div>
                      <p className={`text-sm font-semibold leading-tight ${active ? "text-green-700" : "text-slate-700"}`}>{opt.label}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{opt.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <Divider />

            {/* ── Footer e note ── */}
            <SectionLabel>Footer e note</SectionLabel>
            <p className="text-[11px] text-slate-400 px-4 -mt-1 mb-3">Personalizza le informazioni nel piè di pagina</p>
            <div className="px-4 pb-4">
              {/* Griglia chip — riga 1 */}
              <div className="grid grid-cols-3 gap-2 mb-2">
                {[
                  { key: "blocco_note_automatiche",       label: "Note visibili",       Icon: FileText      },
                  { key: "blocco_riepilogo_iva_dettagliato", label: "Dichiarazione IVA", Icon: Receipt       },
                  { key: "blocco_coordinate_bancarie",    label: "Coordinate pagamento", Icon: Landmark      },
                ].map(({ key, label, Icon }) => {
                  const active = !!layout[key];
                  return (
                    <button
                      key={key}
                      onClick={() => set(key, !active)}
                      className={`flex items-center gap-1.5 px-2 py-2 rounded-lg border text-left transition-all
                        ${active
                          ? "bg-green-50 border-green-200 text-green-700"
                          : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    >
                      {active
                        ? <Check className="w-3 h-3 text-green-500 flex-shrink-0" strokeWidth={3} />
                        : <Icon className="w-3 h-3 flex-shrink-0 text-slate-400" />}
                      <span className="text-[11px] font-medium leading-tight">{label}</span>
                    </button>
                  );
                })}
              </div>
              {/* Griglia chip — riga 2 */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "blocco_scadenze", label: "Scadenze",     Icon: CalendarClock },
                  { key: "blocco_firma",    label: "Firma",         Icon: PenLine       },
                  { key: "blocco_qr_code",  label: "QR Code",       Icon: QrCode        },
                ].map(({ key, label, Icon }) => {
                  const active = !!layout[key];
                  return (
                    <button
                      key={key}
                      onClick={() => set(key, !active)}
                      className={`flex items-center gap-1.5 px-2 py-2 rounded-lg border text-left transition-all
                        ${active
                          ? "bg-green-50 border-green-200 text-green-700"
                          : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"}`}
                    >
                      {active
                        ? <Check className="w-3 h-3 text-green-500 flex-shrink-0" strokeWidth={3} />
                        : <Icon className="w-3 h-3 flex-shrink-0 text-slate-400" />}
                      <span className="text-[11px] font-medium leading-tight">{label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Campo BIC — visibile solo quando QR Code è attivo */}
              {layout.blocco_qr_code && (
                <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                  <p className="text-[11px] font-semibold text-blue-700 mb-1">
                    BIC / SWIFT
                    <span className="font-normal text-blue-500 ml-1">(necessario per il QR EPC)</span>
                  </p>
                  <Input
                    placeholder="Es. BNLIITRR"
                    value={layout.bic || ""}
                    onChange={e => set("bic", e.target.value.toUpperCase())}
                    className="text-sm bg-white h-8"
                    maxLength={11}
                  />
                  <p className="text-[10px] text-blue-400 mt-1">
                    8 o 11 caratteri · codice della tua banca (vedi estratto conto)
                  </p>
                </div>
              )}

              {/* Pulsante modifica testo footer */}
              <button
                onClick={() => setTextEditorOpen(v => !v)}
                className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-800 transition-colors text-[12px] font-medium"
              >
                <Pencil className="w-3.5 h-3.5 text-slate-400" />
                <span className="flex-1 text-left">Modifica testo footer</span>
                {textEditorOpen
                  ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                  : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
              </button>

              {/* Editor testi — espandibile */}
              {textEditorOpen && (
                <div className="mt-3 flex flex-col gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 mb-1">Nota automatica nel documento</p>
                    <Textarea
                      placeholder="Testo mostrato nella sezione Note del PDF…"
                      value={noteText}
                      onChange={e => setNoteText(e.target.value)}
                      rows={2}
                      className="text-sm resize-none bg-white"
                    />
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-slate-500 mb-1">Testo footer custom</p>
                    <Textarea
                      placeholder="Es. Pagamento tramite bonifico bancario…"
                      value={footerText}
                      onChange={e => setFooterText(e.target.value)}
                      rows={2}
                      className="text-sm resize-none bg-white"
                    />
                  </div>
                </div>
              )}
            </div>

          </div>{/* /scrollable */}

          {/* Salva fisso in fondo */}
          <div className="px-4 py-3 border-t border-slate-100 bg-white shrink-0">
            <Button className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva aspetto
            </Button>
          </div>
        </div>

        {/* ════════════════════════════════════════════
            PANNELLO ANTEPRIMA — destra
        ════════════════════════════════════════════ */}
        <div className={embedded ? "flex-1 min-w-0 min-h-0 flex flex-col gap-3" : "flex-1 min-w-0 flex flex-col gap-3"}>
          {/* Toolbar */}
          <div className="flex items-center justify-between shrink-0">
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
              {[
                { id: "desktop", icon: Monitor,    label: "Desktop A4" },
                { id: "mobile",  icon: Smartphone, label: "Mobile" },
                { id: "print",   icon: Printer,    label: "Stampa" },
              ].map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setPreviewMode(id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-all
                    ${previewMode === id ? "bg-slate-800 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 hidden sm:inline">Anteprima live — aggiornamento immediato</span>
              <Button size="sm" variant="outline" onClick={handlePdfPreview} disabled={pdfLoading} className="shadow-sm">
                {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileSearch className="w-4 h-4 mr-1.5" />}
                Anteprima PDF completa
              </Button>
            </div>
          </div>

          {/* Area preview */}
          <div
            className={`overflow-auto rounded-xl border border-slate-200 bg-slate-100 flex items-start justify-center p-8 shadow-inner
              ${embedded ? "flex-1 min-h-0" : previewMode === "mobile" ? "min-h-[700px]" : "min-h-[900px]"}`}
          >
            <AnteprimaFatturaLive key={layout.template} layout={layout} config={config} mode={previewMode} />
          </div>
        </div>

      </div>
    </div>
  );
}
